import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsPersonPlus, BsPencilSquare, BsTrash, BsXCircle, BsCheckCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';

import MUIModal from '../../components/MUIModal/MUIModal';
import MUITreeAccordion from '../../components/MUITreeAccordion/MUITreeAccordion';
import MUILinerProgress from '../../components/MUILinerProgress/MUILinerProgress';
import { USERS_TABLE_KEYS } from '../../assets/constants/constants';
import {
  addUser,
  deleteUser,
  getUserById,
  getUsers,
  updateUser,
} from '../../redux/slices/usersSlice';
import { getDistricts, getRegions } from '../../redux/slices/selectsSlice';

const styles = {
  page: {
    padding: '1.5rem',
    minHeight: 'calc(100vh - 60px)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#2d3748',
    margin: 0,
  },
  btnGroup: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  btn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  btnWarning: {
    background: 'linear-gradient(135deg, #f6c23e 0%, #e0a800 100%)',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(246, 194, 62, 0.3)',
  },
  btnDanger: {
    background: 'linear-gradient(135deg, #e74a3b 0%, #c0392b 100%)',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(231, 74, 59, 0.3)',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#4e73df',
    margin: 0,
  },
  content: {
    display: 'flex',
    gap: '0',
    minHeight: '500px',
  },
  sidebar: {
    width: '280px',
    borderRight: '1px solid #e2e8f0',
    overflowY: 'auto',
    maxHeight: '600px',
    flexShrink: 0,
  },
  details: {
    flex: 1,
    padding: '1.5rem',
    overflowY: 'auto',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f1f5f9',
  },
  tableLabel: {
    width: '40%',
    fontWeight: 500,
    color: '#64748b',
    fontSize: '0.85rem',
  },
  tableValue: {
    width: '60%',
    color: '#1e293b',
    fontSize: '0.9rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.85rem',
    transition: 'border-color 0.2s ease',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.85rem',
    background: '#fff',
    outline: 'none',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#4e73df',
  },
  badge: active => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: active ? '#d4edda' : '#f8d7da',
    color: active ? '#155724' : '#721c24',
  }),
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    color: '#a0aec0',
    fontSize: '0.95rem',
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#2d3748',
    marginBottom: '1.5rem',
  },
  modalBtns: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
  },
};

const emptyUserForm = {
  username: '',
  lastname: '',
  password: '',
  email: '',
  phone_number: '',
  region_id: '',
  district_id: '',
  access_to_settings_l1: false,
  access_to_settings_l2: false,
  access_to_settings_l3: false,
  access_to_settings_l4: false,
};

function Users() {
  const dispatch = useDispatch();
  const {
    users: { isLoading, users, currentUser },
    selects,
    auth: { userInfo },
  } = useSelector(store => store);
  const [action, setAction] = useState('view');
  const [user, setUser] = useState(emptyUserForm);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChangeCurrentUser = userData => {
    if (action !== 'view') return;
    dispatch(getUserById(userData?.id));
  };

  const handleChange = (name, value) => {
    if (name === 'region') {
      setUser(prev => ({ ...prev, region_id: value, district_id: '' }));
      if (value) dispatch(getDistricts(value));
      return;
    }
    if (name === 'district') {
      setUser(prev => ({ ...prev, district_id: value }));
      return;
    }
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (action === 'add') {
        if (!user.username?.trim()) {
          toast.error('Login majburiy');
          return;
        }
        if (!user.password) {
          toast.error('Parol majburiy');
          return;
        }
        const parentId = currentUser?.id || userInfo?.user_id;
        if (!parentId) {
          toast.error('Parent aniqlanmadi. Qayta login qiling');
          return;
        }
        const payload = {
          ...user,
          parent_id: parentId,
          region_id: user.region_id || null,
          district_id: user.district_id || null,
        };
        await dispatch(addUser(payload)).unwrap();
        toast.success('Foydalanuvchi yaratildi');
      } else {
        if (user.username !== undefined && !String(user.username).trim()) {
          toast.error('Login majburiy');
          return;
        }
        await dispatch(updateUser({ id: currentUser?.id, user })).unwrap();
        await dispatch(getUsers());
        toast.success('Foydalanuvchi yangilandi');
      }
      setAction('view');
      setUser(emptyUserForm);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Xatolik yuz berdi');
    }
  };

  const handleDelete = async id => {
    try {
      await dispatch(deleteUser(id)).unwrap();
      handleClose();
      toast.success("Foydalanuvchi o'chirildi");
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Xatolik yuz berdi');
    }
  };

  const handleCancel = () => {
    setAction('view');
    setUser(emptyUserForm);
  };

  const startAdd = () => {
    setAction('add');
    setUser(emptyUserForm);
    dispatch(getRegions());
  };

  const startEdit = () => {
    setAction('edit');
    setUser({
      username: currentUser?.username || '',
      lastname: currentUser?.lastname || '',
      password: '',
      email: currentUser?.email || '',
      phone_number: currentUser?.phone_number || '',
      region_id: currentUser?.region_id || '',
      district_id: currentUser?.district_id || '',
      access_to_settings_l1: !!currentUser?.access_to_settings_l1,
      access_to_settings_l2: !!currentUser?.access_to_settings_l2,
      access_to_settings_l3: !!currentUser?.access_to_settings_l3,
      access_to_settings_l4: !!currentUser?.access_to_settings_l4,
    });
    dispatch(getRegions());
    if (currentUser?.region_id) {
      dispatch(getDistricts(currentUser.region_id));
    }
  };

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (action === 'view' && currentUser?.id) {
      dispatch(getRegions());
      if (currentUser?.region_id) {
        dispatch(getDistricts(currentUser.region_id));
      }
    }
  }, [currentUser?.id, action, dispatch]);

  const showForm = action === 'add' || action === 'edit' || currentUser?.id;

  const renderFieldValue = key => {
    if (action === 'add' || action === 'edit') {
      if (key?.type === 'input') {
        return (
          <input
            id={key.name}
            type={key.name === 'password' ? 'password' : 'text'}
            style={styles.input}
            placeholder={
              key.name === 'password' && action === 'edit'
                ? "O'zgartirmasangiz bo'sh qoldiring"
                : `${key.label || key.name} kiriting...`
            }
            value={user?.[key.name] ?? ''}
            onChange={e => handleChange(key.name, e.target.value)}
          />
        );
      }
      if (key?.type === 'select') {
        const options = selects?.[key.name] || [];
        return (
          <select
            style={styles.select}
            value={user?.[`${key.name}_id`] || ''}
            onChange={e => handleChange(key.name, e.target.value)}
          >
            <option value=''>{key.label || key.name} tanlang</option>
            {options.map(option => (
              <option key={option?.id} value={option.id}>
                {option?.[`${key.name}_name`]}
              </option>
            ))}
          </select>
        );
      }
      if (key?.type === 'checkbox') {
        return (
          <input
            id={key.name}
            type='checkbox'
            style={styles.checkbox}
            checked={!!user?.[key.name]}
            onChange={() => handleChange(key.name, !user?.[key.name])}
          />
        );
      }
    }

    if (key?.type === 'checkbox') {
      return (
        <span style={styles.badge(currentUser?.[key.name])}>
          {currentUser?.[key.name] ? 'Faol' : 'Nofaol'}
        </span>
      );
    }
    if (key.name === 'password') {
      return <span>••••••••</span>;
    }
    return <span>{currentUser?.[key.name] || '—'}</span>;
  };

  return (
    <>
      <MUIModal open={open}>
        <div style={styles.modal}>
          <h2 style={styles.modalTitle}>
            Haqiqatan ham bu foydalanuvchini o&apos;chirmoqchimisiz?
          </h2>
          <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
            <strong>{currentUser?.username}</strong> foydalanuvchisi o&apos;chiriladi
          </p>
          <div style={styles.modalBtns}>
            <button
              type='button'
              onClick={() => handleDelete(currentUser?.id)}
              style={{ ...styles.btn, ...styles.btnDanger, padding: '0.6rem 2rem' }}
            >
              <BsTrash /> Ha, o&apos;chirish
            </button>
            <button
              type='button'
              onClick={handleClose}
              style={{
                ...styles.btn,
                background: '#e2e8f0',
                color: '#4a5568',
                padding: '0.6rem 2rem',
              }}
            >
              Bekor qilish
            </button>
          </div>
        </div>
      </MUIModal>

      <div style={styles.page}>
        {isLoading && <MUILinerProgress />}

        <div style={styles.header}>
          <h1 style={styles.title}>Foydalanuvchilar</h1>
          <div style={styles.btnGroup}>
            {action === 'view' ? (
              <>
                <button
                  type='button'
                  onClick={startAdd}
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                >
                  <BsPersonPlus /> Qo&apos;shish
                </button>
                {currentUser?.id && (
                  <>
                    <button
                      type='button'
                      onClick={startEdit}
                      style={{ ...styles.btn, ...styles.btnWarning }}
                    >
                      <BsPencilSquare /> Tahrirlash
                    </button>
                    <button
                      type='button'
                      onClick={handleOpen}
                      style={{ ...styles.btn, ...styles.btnDanger }}
                    >
                      <BsTrash /> O&apos;chirish
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  type='button'
                  onClick={handleSave}
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                >
                  <BsCheckCircle /> Saqlash
                </button>
                <button
                  type='button'
                  onClick={handleCancel}
                  style={{
                    ...styles.btn,
                    background: '#e2e8f0',
                    color: '#4a5568',
                  }}
                >
                  <BsXCircle /> Bekor qilish
                </button>
              </>
            )}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h4 style={styles.cardTitle}>
              {action === 'add'
                ? 'Yangi foydalanuvchi'
                : action === 'edit'
                  ? 'Tahrirlash'
                  : "Foydalanuvchilar ro'yxati"}
            </h4>
          </div>

          <div style={styles.content}>
            <div style={styles.sidebar}>
              {users && users.length > 0 ? (
                <MUITreeAccordion
                  data={users}
                  onChangeCurrentUser={currUser => handleChangeCurrentUser(currUser)}
                />
              ) : (
                <div style={styles.emptyState}>
                  <p>Foydalanuvchilar topilmadi</p>
                  <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    &quot;Qo&apos;shish&quot; orqali birinchisini yarating
                  </p>
                </div>
              )}
            </div>

            <div style={styles.details}>
              {showForm ? (
                USERS_TABLE_KEYS.map(key => (
                  <div key={key?.id} style={styles.tableRow}>
                    <div style={styles.tableLabel}>{key.label || key.name}</div>
                    <div style={styles.tableValue}>{renderFieldValue(key)}</div>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>
                  <p>Foydalanuvchini tanlang yoki yangisini qo&apos;shing</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Users;
